import React, { useState, useContext, useEffect, useCallback, useMemo, useRef } from "react";
import { useTranslation } from 'react-i18next';
import { Box } from "@mui/joy";
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import { PickersDay } from "@mui/x-date-pickers";
import dayjs from "dayjs";
import { AdapterDayjs } from "@mui/x-date-pickers-pro/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { StaticDateRangePicker } from "@mui/x-date-pickers-pro/StaticDateRangePicker";
import { BookingsCountDialog } from "../Dialog";
import { Button } from '../Button';
import { itIT, enUS } from '@mui/x-date-pickers/locales';
import { AppContext } from "src/mtska-frontend-app-scaffolding";
import { useDataProvider } from "src/mtska-frontend-data-provider";
import { Text } from '../Typography';

const FILTER_DEBOUNCE_MS = 400;

const OCCUPANCY_COLORS = {
    green: {
        backgroundColor: 'rgba(76, 175, 80, 0.35)',
        color: 'rgba(0, 0, 0, 0.87)',
        borderColor: 'rgba(76, 175, 80, 0.5)',
    },
    yellow: {
        backgroundColor: 'rgba(255, 193, 7, 0.45)',
        color: 'rgba(0, 0, 0, 0.87)',
        borderColor: 'rgba(255, 193, 7, 0.6)',
    },
    orange: {
        backgroundColor: 'rgba(255, 152, 0, 0.5)',
        color: 'rgba(0, 0, 0, 0.87)',
        borderColor: 'rgba(255, 152, 0, 0.65)',
    },
    red: {
        backgroundColor: 'rgba(244, 67, 54, 0.55)',
        color: '#fff',
        borderColor: 'rgba(244, 67, 54, 0.7)',
    },
};

const SELECTION_STYLES = {
    normalRange: {
        backgroundColor: 'rgba(25, 118, 210, 0.12)',
        borderColor: 'rgba(25, 118, 210, 0.12)',
    },
    normalStartEnd: {
        backgroundColor: 'rgba(25, 118, 210, 1)',
        color: 'white',
    },
};

const CustomPickersDay = React.memo((props) => {
    const { t } = useTranslation();
    const {
        day,
        outsideCurrentMonth,
        selected,
        selectedDays = [],
        dailyOccupancy = {},
        ...other
    } = props;

    const dateStr = useMemo(() => day.format("YYYY-MM-DD"), [day]);
    const occupancy = useMemo(() => dailyOccupancy[dateStr], [dailyOccupancy, dateStr]);
    const [start, end] = useMemo(() => selectedDays.map((d) => dayjs(d)), [selectedDays]);

    const isRangeStart = useMemo(() => selected && start?.isSame(day, 'day'), [selected, start, day]);
    const isRangeEnd = useMemo(() => selected && end?.isSame(day, 'day'), [selected, end, day]);
    const isInRange = useMemo(
        () => start && end && day.isAfter(start, 'day') && day.isBefore(end, 'day'),
        [start, end, day]
    );
    const isSelectedRange = isRangeStart || isRangeEnd || isInRange;

    const dayStyle = useMemo(() => {
        const base = {
            borderRadius: 0,
            border: '1px solid transparent',
        };

        if (isSelectedRange) {
            if (isRangeStart || isRangeEnd) {
                return {
                    ...base,
                    ...SELECTION_STYLES.normalStartEnd,
                    '&&.Mui-selected': {
                        backgroundColor: SELECTION_STYLES.normalStartEnd.backgroundColor,
                        color: 'white',
                        borderTopLeftRadius: isRangeStart ? '50%' : 0,
                        borderBottomLeftRadius: isRangeStart ? '50%' : 0,
                        borderTopRightRadius: isRangeEnd ? '50%' : 0,
                        borderBottomRightRadius: isRangeEnd ? '50%' : 0,
                    },
                };
            }

            return {
                ...base,
                ...SELECTION_STYLES.normalRange,
            };
        }

        if (occupancy?.level && OCCUPANCY_COLORS[occupancy.level]) {
            return {
                ...base,
                ...OCCUPANCY_COLORS[occupancy.level],
            };
        }

        return base;
    }, [isSelectedRange, isRangeStart, isRangeEnd, isInRange, occupancy]);

    const tooltipContent = occupancy ? (
        <Box>
            {t('{{booked}} / {{total}} vehicles booked', {
                booked: occupancy.booked ?? 0,
                total: occupancy.total ?? 0,
            })}
        </Box>
    ) : null;

    return (
        <Tooltip
            title={tooltipContent}
            arrow
            placement="top"
            enterDelay={300}
            leaveDelay={200}
            disableInteractive
            PopperProps={{
                sx: {
                    '& .MuiTooltip-tooltip': {
                        bgcolor: 'rgba(97, 97, 97, 0.9)',
                        fontSize: '0.8rem',
                        maxWidth: '200px',
                    },
                },
            }}
        >
            <span>
                <PickersDay
                    {...other}
                    day={day}
                    outsideCurrentMonth={outsideCurrentMonth}
                    selected={selected}
                    sx={dayStyle}
                />
            </span>
        </Tooltip>
    );
});

const WidgetBookingsCountCalendar = () => {
    const { t, i18n } = useTranslation();

    const [dateRange, setDateRange] = useState([null, null]);
    const [hasSelectionCompleted, setHasSelectionCompleted] = useState(false);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [dailyOccupancy, setDailyOccupancy] = useState({});
    const [driverName, setDriverName] = useState('');
    const [legalEntityName, setLegalEntityName] = useState('');
    const [appliedDriverName, setAppliedDriverName] = useState('');
    const [appliedLegalEntityName, setAppliedLegalEntityName] = useState('');
    const loadedRangeRef = useRef({ start: null, end: null });

    const { innerWidth } = useContext(AppContext);
    const { items: occupancyData, loadAll } = useDataProvider('calendars/carcalendarrecord/dailyoccupancy', false);

    useEffect(() => {
        const timer = setTimeout(() => {
            setAppliedDriverName(driverName.trim());
            setAppliedLegalEntityName(legalEntityName.trim());
        }, FILTER_DEBOUNCE_MS);

        return () => clearTimeout(timer);
    }, [driverName, legalEntityName]);

    const calendarFilters = useMemo(() => {
        const filters = {};
        if (appliedDriverName) {
            filters.driver_name = appliedDriverName;
        }
        if (appliedLegalEntityName) {
            filters.legal_entity_name = appliedLegalEntityName;
        }
        return filters;
    }, [appliedDriverName, appliedLegalEntityName]);

    const fetchOccupancy = useCallback((start, end) => {
        if (!start || !end) {
            return;
        }

        loadAll({
            bookingStart_at: start.format('YYYY-MM-DDTHH:mm:ss'),
            bookingEnd_at: end.format('YYYY-MM-DDTHH:mm:ss'),
            ...calendarFilters,
        });
    }, [loadAll, calendarFilters]);

    const ensureOccupancyLoaded = useCallback((anchorMonth) => {
        const anchor = dayjs(anchorMonth ?? undefined);
        const rangeStart = anchor.subtract(3, 'month').startOf('month');
        const rangeEnd = anchor.add(9, 'month').endOf('month');

        const { start: loadedStart, end: loadedEnd } = loadedRangeRef.current;
        if (
            loadedStart &&
            loadedEnd &&
            !rangeStart.isBefore(loadedStart, 'day') &&
            !rangeEnd.isAfter(loadedEnd, 'day')
        ) {
            return;
        }

        loadedRangeRef.current = { start: rangeStart, end: rangeEnd };
        fetchOccupancy(rangeStart, rangeEnd);
    }, [fetchOccupancy]);

    useEffect(() => {
        loadedRangeRef.current = { start: null, end: null };
        setDailyOccupancy({});
        ensureOccupancyLoaded(dayjs());
    }, [calendarFilters, ensureOccupancyLoaded]);

    useEffect(() => {
        if (occupancyData?.days) {
            setDailyOccupancy((prev) => ({ ...prev, ...occupancyData.days }));
        }
    }, [occupancyData]);

    const handleMonthChange = useCallback((month) => {
        ensureOccupancyLoaded(dayjs(month));
    }, [ensureOccupancyLoaded]);

    const refreshOccupancy = useCallback(() => {
        const { start, end } = loadedRangeRef.current;
        if (start && end) {
            fetchOccupancy(start, end);
            return;
        }

        ensureOccupancyLoaded(dayjs());
    }, [fetchOccupancy, ensureOccupancyLoaded]);

    const onChange = (newValue) => {
        if (hasSelectionCompleted && newValue[0]) {
            setDateRange([newValue[0], null]);
            setHasSelectionCompleted(false);
            return;
        }

        if (newValue[0] && newValue[1]) {
            setDateRange([newValue[0], newValue[1]]);
            setHasSelectionCompleted(true);
            setDialogOpen(true);
        } else {
            setDateRange(newValue);
        }
    };

    const legendItems = useMemo(() => ([
        { level: 'green', label: t('Green: <50% occupied') },
        { level: 'yellow', label: t('Yellow: 50%–80% occupied') },
        { level: 'orange', label: t('Orange: >80% occupied') },
        { level: 'red', label: t('Red: fully booked') },
    ]), [t]);

    const hasActiveFilters = Boolean(driverName.trim() || legalEntityName.trim());

    const handleClearFilters = () => {
        setDriverName('');
        setLegalEntityName('');
    };

    return (
        <Box className="form-element form-element-calendar-booking-widget col-12">
            <Box className="form-element-calendar-booking-widget-content">
                <Box className="form-element-calendar-booking-widget-filters">
                    <TextField
                        label={t('Driver')}
                        size="small"
                        value={driverName}
                        onChange={(event) => setDriverName(event.target.value)}
                        style={{ flex: 1, minWidth: 220 }}
                    />
                    <TextField
                        label={t('legal_entity')}
                        size="small"
                        value={legalEntityName}
                        onChange={(event) => setLegalEntityName(event.target.value)}
                        style={{ flex: 1, minWidth: 220 }}
                    />
                    {hasActiveFilters && (
                        <Button
                            variant="outlined"
                            size="small"
                            label={t('Clear filters')}
                            onClick={handleClearFilters}
                        />
                    )}
                </Box>

                <Box className="form-element-calendar-booking-widget-calendar">
                    <LocalizationProvider
                        dateAdapter={AdapterDayjs}
                        adapterLocale={i18n.resolvedLanguage === 'it' ? itIT : enUS}
                    >
                        <StaticDateRangePicker
                            value={dateRange}
                            onChange={onChange}
                            onMonthChange={handleMonthChange}
                            slots={{
                                day: CustomPickersDay,
                                toolbar: () => null,
                                actionBar: () => null,
                            }}
                            localeText={i18n.resolvedLanguage === 'it'
                                ? itIT.components.MuiLocalizationProvider.defaultProps.localeText
                                : enUS.components.MuiLocalizationProvider.defaultProps.localeText
                            }
                            slotProps={{
                                day: (ownerState) => ({
                                    selectedDays: ownerState.selectedDays || [],
                                    dailyOccupancy,
                                }),
                            }}
                            calendars={innerWidth < 600 ? 1 : 4}
                            sx={{
                                width: '100%',
                                '& .MuiPickersLayout-root': {
                                    display: 'flex',
                                    flexDirection: 'column',
                                    width: '100%',
                                },
                                '& .MuiPickersLayout-toolbar': {
                                    display: 'none',
                                },
                                '& .MuiPickersLayout-actionBar': {
                                    display: 'none',
                                },
                                '& .MuiPickersLayout-contentWrapper': {
                                    overflow: 'visible',
                                },
                                '& .MuiDateRangeCalendar-root': {
                                    justifyContent: 'center',
                                    flexWrap: 'wrap',
                                    alignItems: 'flex-start',
                                },
                                '& .MuiDateRangeCalendar-monthContainer': {
                                    overflow: 'visible',
                                },
                                '& .MuiPickersSlideTransition-root': {
                                    minHeight: '252px',
                                    height: 'auto !important',
                                    overflow: 'visible',
                                },
                                '& .MuiDayCalendar-slideTransition': {
                                    minHeight: '252px',
                                    height: 'auto !important',
                                    overflow: 'visible',
                                },
                                '& .MuiPickersDay-root': {
                                    margin: '0px',
                                },
                            }}
                        />
                    </LocalizationProvider>
                </Box>

                <Box className="form-element-calendar-booking-widget-legend">
                    {legendItems.map(({ level, label }) => (
                        <Box key={level} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Box
                                sx={{
                                    width: 16,
                                    height: 16,
                                    borderRadius: '4px',
                                    border: '1px solid',
                                    ...OCCUPANCY_COLORS[level],
                                }}
                            />
                            <Text level="body-sm">{label}</Text>
                        </Box>
                    ))}
                </Box>
            </Box>

            <BookingsCountDialog
                open={dialogOpen}
                onClose={() => setDialogOpen(false)}
                dateRange={dateRange}
                calendarFilters={calendarFilters}
                onOccupancyRefresh={refreshOccupancy}
            />
        </Box>
    );
};

export default WidgetBookingsCountCalendar;
